import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { HelpCircle, Send, MessageSquare, FileText, Loader2, CheckCircle, Clock, Sparkles, Shield, HeadphonesIcon, RefreshCw, Users, Ticket, BookOpen } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Professional header image
const HEADER_IMAGE_URL = "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=1600&q=80";

// Feature highlights
const SUPPORT_FEATURES = [
  { icon: MessageSquare, label: "Contact Support", description: "Get help fast", color: "text-blue-500", bg: "bg-blue-50", gradient: "from-blue-500 to-indigo-500" },
  { icon: Ticket, label: "Track Tickets", description: "Monitor requests", color: "text-purple-500", bg: "bg-purple-50", gradient: "from-purple-500 to-pink-500" },
  { icon: BookOpen, label: "FAQ", description: "Quick answers", color: "text-green-500", bg: "bg-green-50", gradient: "from-green-500 to-emerald-500" },
  { icon: HeadphonesIcon, label: "24/7 Support", description: "Always available", color: "text-amber-500", bg: "bg-amber-50", gradient: "from-amber-500 to-orange-500" }
];

const SupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'technical',
    priority: 'medium',
    message: ''
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/support/tickets`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Support ticket submitted successfully!');
      setFormData({ subject: '', category: 'technical', priority: 'medium', message: '' });
      fetchTickets();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSupport = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/support/contact`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Message sent successfully! We will get back to you soon.');
      setFormData({ subject: '', category: 'technical', priority: 'medium', message: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const faqItems = [
    {
      question: 'How do I upgrade my subscription?',
      answer: 'Go to Billing & Subscription page and click "Manage Billing Portal" to upgrade your plan.'
    },
    {
      question: 'How can I cancel my subscription?',
      answer: 'Navigate to Billing & Subscription, then click "Cancel Subscription". You will retain access until the end of your billing period.'
    },
    {
      question: 'How do I add more users to my account?',
      answer: 'Additional users can be added from the Billing page. Pricing varies by plan: Starter ($19.99/mo, up to 25 users), Professional ($29.99/mo, up to 100 users), or Enterprise ($49.99/mo, unlimited users).'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards through our secure Stripe payment processor.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes, all data is encrypted in transit and at rest. We follow industry-standard security practices and are OSHA compliant.'
    },
    {
      question: 'Can I export my audit data?',
      answer: 'Yes, you can generate and download reports from the Reports page in PDF format.'
    },
    {
      question: 'How does the AI safety analysis work?',
      answer: 'Our AI analyzes images and videos to detect safety violations, PPE compliance issues, and potential hazards using advanced computer vision.'
    },
    {
      question: 'What is the refund policy?',
      answer: 'We offer a 14-day money-back guarantee. Contact support for refund requests within 14 days of purchase.'
    }
  ];

  // Calculate stats
  const stats = {
    totalTickets: tickets.length,
    openTickets: tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length,
    resolvedTickets: tickets.filter(t => t.status === 'resolved').length,
    faqCount: faqItems.length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Stylized Header with Visual */}
        <div className="relative rounded-2xl overflow-hidden shadow-xl">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HEADER_IMAGE_URL})` }}
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/95 via-blue-900/90 to-indigo-900/85" />
          
          {/* Content */}
          <div className="relative px-8 py-10">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg">
                    <HeadphonesIcon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 px-3 py-1">
                      <Sparkles className="w-3 h-3 mr-1" />
                      24/7 Support
                    </Badge>
                    <Badge className="bg-green-500/20 text-green-300 border-green-500/30 px-3 py-1">
                      <Shield className="w-3 h-3 mr-1" />
                      Priority Help
                    </Badge>
                  </div>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                  Support Center
                </h1>
                <p className="text-lg text-cyan-100/90 max-w-2xl">
                  Get help, submit tickets, and find answers to common questions
                </p>
              </div>
              
              {/* Quick Stats in Header */}
              <div className="hidden lg:flex gap-3">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                  <Ticket className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats.totalTickets}</p>
                  <p className="text-xs text-cyan-200">Total Tickets</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                  <Clock className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats.openTickets}</p>
                  <p className="text-xs text-cyan-200">Open</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 text-center min-w-[100px]">
                  <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stats.resolvedTickets}</p>
                  <p className="text-xs text-cyan-200">Resolved</p>
                </div>
              </div>
            </div>
            
            {/* Feature Pills */}
            <div className="flex flex-wrap gap-2 mt-6">
              {SUPPORT_FEATURES.map((feature, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20"
                >
                  <feature.icon className="w-4 h-4 text-cyan-300" />
                  <span className="text-sm text-white/90">{feature.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Feature Cards Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex gap-2">
            {SUPPORT_FEATURES.map((feature, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl ${feature.bg} cursor-pointer hover:shadow-md transition-all`}
              >
                <feature.icon className={`w-5 h-5 ${feature.color}`} />
                <div>
                  <p className="text-sm font-medium text-gray-900">{feature.label}</p>
                  <p className="text-xs text-gray-500">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="rounded-2xl shadow-sm border-t-4 border-t-blue-500 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Total Tickets</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalTickets}</p>
                  <p className="text-xs text-gray-400 mt-1">submitted</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                  <Ticket className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm border-t-4 border-t-amber-500 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Open Tickets</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.openTickets}</p>
                  <p className="text-xs text-gray-400 mt-1">in progress</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm border-t-4 border-t-green-500 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Resolved</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.resolvedTickets}</p>
                  <p className="text-xs text-gray-400 mt-1">completed</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm border-t-4 border-t-purple-500 hover:shadow-lg transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">FAQ Items</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.faqCount}</p>
                  <p className="text-xs text-gray-400 mt-1">quick answers</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="contact" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 rounded-xl bg-gray-100 p-1">
            <TabsTrigger value="contact" className="rounded-lg">Contact Support</TabsTrigger>
            <TabsTrigger value="tickets" className="rounded-lg">My Tickets</TabsTrigger>
            <TabsTrigger value="faq" className="rounded-lg">FAQ</TabsTrigger>
          </TabsList>

          {/* Contact Support Tab */}
          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle>Contact Support</CardTitle>
                <CardDescription>Send us a message and we&apos;ll get back to you within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSupport} className="space-y-4">
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Brief description of your issue"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <select
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full border rounded-md p-2"
                      >
                        <option value="technical">Technical Issue</option>
                        <option value="billing">Billing Question</option>
                        <option value="feature">Feature Request</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="priority">Priority</Label>
                      <select
                        id="priority"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="w-full border rounded-md p-2"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Provide details about your issue or question"
                      rows={6}
                      required
                    />
                  </div>

                  <Button type="submit" disabled={loading} className="w-full">
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Message</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tickets Tab */}
          <TabsContent value="tickets">
            <Card>
              <CardHeader>
                <CardTitle>My Support Tickets</CardTitle>
                <CardDescription>Track the status of your support requests</CardDescription>
              </CardHeader>
              <CardContent>
                {tickets.length > 0 ? (
                  <div className="space-y-3">
                    {tickets.map((ticket) => (
                      <div key={ticket.id} className="border rounded-lg p-4 hover:bg-slate-50">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-semibold">{ticket.subject}</h3>
                            <p className="text-sm text-slate-600">Ticket #{ticket.id.substring(0, 8)}</p>
                          </div>
                          <Badge
                            variant={
                              ticket.status === 'resolved' ? 'default' :
                              ticket.status === 'in_progress' ? 'secondary' : 'outline'
                            }
                          >
                            {ticket.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-700 mb-2">{ticket.message}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="capitalize">{ticket.category}</span>
                          <span>•</span>
                          <span className="capitalize">{ticket.priority} priority</span>
                          <span>•</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-600">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <p>No support tickets yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
                <CardDescription>Quick answers to common questions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {faqItems.map((item, index) => (
                    <div key={index} className="border-b pb-4 last:border-b-0">
                      <h3 className="font-semibold text-slate-900 mb-2 flex items-start gap-2">
                        <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        {item.question}
                      </h3>
                      <p className="text-slate-700 ml-7">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SupportPage;